import { clamp } from '@theme/utilities';

/**
 * Magnifies a product image on hover: a lens follows the cursor over the image
 * while a separate zoomed-in preview renders in a floating panel to the right.
 * Desktop (mouse + hover-capable, enough viewport space) only.
 */

const CONTAINER_SELECTOR = '.product-media-container--hover-zoom';
const ZOOM_FACTOR = 2.5;
const PANEL_SIZE = 480;
const PANEL_GAP = 24;
const MIN_PANEL_SIZE = 220;
const VIEWPORT_MARGIN = 16;

/**
 * @typedef {object} MagnifierGeometry
 * @property {DOMRect} rect - The visible (rendered) box of the source image.
 * @property {number} panelSize - Fixed square size of the preview panel.
 * @property {number} lensSize - Fixed square size of the lens on the source image.
 * @property {number} scale - panelSize / lensSize.
 * @property {number} displayedWidth - Natural image size once scaled to cover `rect` (pre-crop).
 * @property {number} displayedHeight
 * @property {number} cropOffsetX - How far the cover-scaled image is shifted outside `rect` on the x-axis.
 * @property {number} cropOffsetY
 * @property {string} imageSource
 */

/** @type {HTMLElement | null} */
let activeContainer = null;
/** @type {MagnifierGeometry | null} */
let geometry = null;
/** @type {HTMLDivElement | null} */
let lensEl = null;
/** @type {HTMLDivElement | null} */
let panelEl = null;

const supportsHoverZoom = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function ensureElements() {
  if (lensEl && panelEl) return;

  lensEl = document.createElement('div');
  lensEl.className = 'hover-zoom-lens';
  lensEl.setAttribute('aria-hidden', 'true');

  panelEl = document.createElement('div');
  panelEl.className = 'hover-zoom-panel';
  panelEl.setAttribute('aria-hidden', 'true');

  document.body.append(lensEl, panelEl);
}

function hideMagnifier() {
  lensEl?.classList.remove('is-visible');
  panelEl?.classList.remove('is-visible');
}

/**
 * Reads the rendered object-position as [x, y] fractions (0-1), defaulting to center.
 * @param {HTMLImageElement} image
 * @returns {[number, number]}
 */
function getObjectPositionFraction(image) {
  const matches = getComputedStyle(image).objectPosition.match(/-?\d+(\.\d+)?%/g);
  if (!matches || matches.length < 2) return [0.5, 0.5];

  const x = parseFloat(matches[0]) / 100;
  const y = parseFloat(matches[1]) / 100;

  return [Number.isFinite(x) ? x : 0.5, Number.isFinite(y) ? y : 0.5];
}

/**
 * Computes the geometry needed to render the lens and panel for a given container.
 * The image is typically shown with `object-fit: cover` inside a box whose aspect
 * ratio doesn't match the source file, so the panel has to replicate that same
 * crop (not just stretch the raw file) or the magnified view looks distorted.
 * @param {HTMLElement} container
 * @returns {MagnifierGeometry | null}
 */
function computeGeometry(container) {
  const image = container.querySelector('.product-media__image');
  if (!(image instanceof HTMLImageElement) || !image.complete || !image.naturalWidth) return null;

  const rect = image.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;

  // The panel is a fixed square; shrink it only if the viewport is too cramped to fit it,
  // and bail entirely once it gets too small to be useful.
  const availableWidth = window.innerWidth - rect.right - PANEL_GAP - VIEWPORT_MARGIN;
  const availableHeight = window.innerHeight - VIEWPORT_MARGIN * 2;
  const panelSize = Math.min(PANEL_SIZE, availableWidth, availableHeight);

  if (panelSize < MIN_PANEL_SIZE) return null;

  // The lens is a matching square on the source image, sized so it never exceeds the
  // image bounds even when the image itself isn't square.
  const lensSize = Math.min(rect.width, rect.height) / ZOOM_FACTOR;
  const scale = panelSize / lensSize;

  // Mirror object-fit: cover — scale the natural image so it fully covers `rect`,
  // then work out how far that scaled image spills outside `rect` on each axis.
  const coverScale = Math.max(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
  const displayedWidth = image.naturalWidth * coverScale;
  const displayedHeight = image.naturalHeight * coverScale;

  const [positionX, positionY] = getObjectPositionFraction(image);
  const cropOffsetX = (displayedWidth - rect.width) * positionX;
  const cropOffsetY = (displayedHeight - rect.height) * positionY;

  return {
    rect,
    panelSize,
    lensSize,
    scale,
    displayedWidth,
    displayedHeight,
    cropOffsetX,
    cropOffsetY,
    imageSource: image.currentSrc || image.src,
  };
}

/** @param {PointerEvent} event */
function updateMagnifier(event) {
  if (!geometry) return;

  const { rect, panelSize, lensSize, scale, displayedWidth, displayedHeight, cropOffsetX, cropOffsetY, imageSource } = geometry;

  ensureElements();
  if (!lensEl || !panelEl) return;

  const cursorX = clamp(event.clientX - rect.left, 0, rect.width);
  const cursorY = clamp(event.clientY - rect.top, 0, rect.height);

  const lensX = clamp(cursorX - lensSize / 2, 0, rect.width - lensSize);
  const lensY = clamp(cursorY - lensSize / 2, 0, rect.height - lensSize);

  lensEl.style.left = `${rect.left + lensX}px`;
  lensEl.style.top = `${rect.top + lensY}px`;
  lensEl.style.width = `${lensSize}px`;
  lensEl.style.height = `${lensSize}px`;
  lensEl.classList.add('is-visible');

  // Center the panel against the viewport, not the source image - anchoring it to the
  // image's own rect made it jump vertically as the hovered image's height/scroll
  // position changed (different aspect ratio, different image, page scroll). Fixed
  // at viewport-center it stays put in the same right-middle spot every time.
  const panelTop = clamp(
    (window.innerHeight - panelSize) / 2,
    VIEWPORT_MARGIN,
    window.innerHeight - panelSize - VIEWPORT_MARGIN
  );

  panelEl.style.left = `${rect.right + PANEL_GAP}px`;
  panelEl.style.top = `${panelTop}px`;
  panelEl.style.width = `${panelSize}px`;
  panelEl.style.height = `${panelSize}px`;
  panelEl.style.backgroundImage = `url("${imageSource}")`;
  panelEl.style.backgroundSize = `${displayedWidth * scale}px ${displayedHeight * scale}px`;
  panelEl.style.backgroundPosition = `-${(lensX + cropOffsetX) * scale}px -${(lensY + cropOffsetY) * scale}px`;
  panelEl.classList.add('is-visible');
}

/** @param {PointerEvent} event */
function handlePointerMove(event) {
  if (event.pointerType !== 'mouse' || !supportsHoverZoom()) return;

  const target = /** @type {HTMLElement} */ (event.target);
  const container = target.closest instanceof Function ? target.closest(CONTAINER_SELECTOR) : null;

  if (container !== activeContainer) {
    activeContainer = /** @type {HTMLElement | null} */ (container);
    geometry = container instanceof HTMLElement ? computeGeometry(container) : null;
    if (!geometry) hideMagnifier();
  }

  if (geometry) updateMagnifier(event);
}

/** @param {PointerEvent} event */
function handlePointerOut(event) {
  if (!activeContainer) return;

  const relatedTarget = /** @type {Node | null} */ (event.relatedTarget);
  if (relatedTarget && activeContainer.contains(relatedTarget)) return;

  activeContainer = null;
  geometry = null;
  hideMagnifier();
}

document.addEventListener('pointermove', handlePointerMove, { passive: true });
document.addEventListener('pointerout', handlePointerOut, { passive: true });
window.addEventListener('scroll', hideMagnifier, { passive: true, capture: true });
